import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from '@/core/infrastructure/supabase/supabaseClient';
import { Session } from '@supabase/supabase-js';
import { useProfileStore } from '@/core/application/state/useProfileStore';

// Pantallas Raíz
import { LoginScreen } from '@/app/auth/LoginScreen';
import { LandingScreen } from '@/app/auth/LandingScreen';
import { InviteLandingScreen } from '@/app/auth/InviteLandingScreen';
import { HubScreen } from '@/app/hub/HubScreen';
import { AuthDispatcherScreen } from '@/app/auth/AuthDispatcherScreen';

// Guard y Store de Autenticación
import { useAuthStore } from '@/core/application/security/useAuthStore';
import { SecretaryGuard } from '@/shared/components/guards/SecretaryGuard';
import { ClinicianGuard } from '@/shared/components/guards/ClinicianGuard';

// Pantalla Secretaría
import SecretaryDashboard from '@/presentation/screens/secretary/SecretaryDashboard';

// Sub-Enrutadores Estancos
import { ConsumerNavigator } from '@/modules/consumer/navigation/ConsumerNavigator';
import { ProfessionalNavigator } from '@/modules/professional/navigation/ProfessionalNavigator';

function SplashLoader() {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-[#0F172A]">
      <div className="w-11 h-11 rounded-full border-4 border-white/15 border-t-white animate-spin" />
    </div>
  );
}

// ── Rutas protegidas: sólo accesibles con sesión activa ──
function PrivateRoutes() {
  return (
    <Routes>
      <Route path="/auth-dispatcher" element={<AuthDispatcherScreen />} />
      <Route path="/hub"             element={<HubScreen />} />
      <Route path="/invite"          element={<InviteLandingScreen />} />
      <Route path="/app/*"           element={<ConsumerNavigator />} />
      <Route path="/pro/*"           element={<ClinicianGuard><ProfessionalNavigator /></ClinicianGuard>} />
      
      {/* Panel Logístico (Secretaría) */}
      <Route 
        path="/secretaria/*" 
        element={
          <SecretaryGuard>
            <Routes>
              <Route path="dashboard" element={<SecretaryDashboard />} />
              <Route index element={<Navigate to="dashboard" replace />} />
            </Routes>
          </SecretaryGuard>
        } 
      />

      {/* Cualquier ruta no conocida redirige al dispatcher */}
      <Route path="*"                element={<Navigate to="/auth-dispatcher" replace />} />
    </Routes>
  );
}

// ── Rutas públicas: accesibles sin sesión ──
function PublicRoutes() {
  return (
    <Routes>
      <Route path="/"       element={<LandingScreen />} />
      <Route path="/login"  element={<LoginScreen />} />
      <Route path="/invite" element={<InviteLandingScreen />} />
      {/* Directorio público — accesible sin login */}
      <Route path="/app/*"  element={<ConsumerNavigator />} />
      {/* Cualquier ruta no conocida vuelve al landing */}
      <Route path="*"       element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function MainNavigator() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const performBackgroundTasks = async (currentSession: Session) => {
      try {
        // 1. Roles (esperamos a que se seteen para no redirigir por error en los guards)
        await useAuthStore.getState().setSession(currentSession).catch(e =>
          console.error('[MainNavigator] Error cargando roles en background:', e)
        );
        useAuthStore.setState({ isInitialized: true });
        
        useProfileStore.getState().fetchProfile(true).catch(e =>
          console.error('[MainNavigator] Error cargando perfil en background:', e)
        );

        // 2. VALIDACIÓN DE SEGURIDAD (valida token con Supabase)
        // Detecta tokens revocados, cuentas suspendidas o JWTs manipulados.
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        
        if (userError || !user) {
          console.warn('[MainNavigator] Token inválido o revocado. Cerrando sesión...');
          await supabase.auth.signOut();
          setSession(null);
          useAuthStore.getState().clearSession();
          useProfileStore.getState().clearProfile();
        }
      } catch (e) {
        // Si falla la red (offline), no cerramos la sesión.
        console.warn('[MainNavigator] No se pudo validar token (posiblemente offline):', e);
      }
    };

    // ── FASE 1: RENDER RÁPIDO (lectura local, sin red) ─────────────────────────
    supabase.auth.getSession().then(({ data: { session: localSession }, error }) => {
      if (error) {
        console.error('[MainNavigator] Error obteniendo sesión local:', error);
      }

      setSession(localSession);
      setLoading(false);

      if (localSession) {
        performBackgroundTasks(localSession);
      } else {
        useAuthStore.getState().clearSession();
        useProfileStore.getState().clearProfile();
        useAuthStore.setState({ isInitialized: true });
      }
    });

    // ── ESCUCHAR CAMBIOS DE SESIÓN (login / logout / token refresh) ───────────
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setLoading(false);

      if (session) {
        performBackgroundTasks(session);
      } else {
        useAuthStore.getState().clearSession();
        useProfileStore.getState().clearProfile();
        useAuthStore.setState({ isInitialized: true });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) return <SplashLoader />;

  return session?.user ? <PrivateRoutes /> : <PublicRoutes />;
}

